import type { AnchorHTMLAttributes, ButtonHTMLAttributes } from 'react';

import {
  DownloadIcon,
  IconButton,
  Paragraph,
  PlusIcon,
  Spinner,
  Tooltip,
} from '@movingimage-evp/mi-ui-component-library';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { InfoTooltip } from 'src/components/info-tooltip';

import {
  ReportStatus,
  ReportType,
  useGenerateReportMutation,
  useGetWebcastReportQuery,
} from '../../../../../../generated/graphql-manager';
import { keycloak } from '../../../../../../service/authentication/keycloak-provider';
import { downloadFile } from '../../../../../../utils';
import { useUserPermissions } from '../../../../../hooks/user-permissions';

import styles from './download-report.module.css';

export type DownloadReportProps = {
  webcastId: string;
  label: string;
  generateLabel: string;
  infoText: string;
  reportType: ReportType;
  disabled?: boolean;
  disabledTooltipLabel?: string;
} & ButtonHTMLAttributes<HTMLButtonElement> &
  AnchorHTMLAttributes<HTMLAnchorElement>;

export function DownloadReport({
  webcastId,
  label,
  generateLabel,
  reportType,
  infoText,
  disabled = false,
  disabledTooltipLabel,
  ...props
}: DownloadReportProps) {
  const { t } = useTranslation();
  const { isEventReportingAllowed } = useUserPermissions();

  const { data, loading, refetch } = useGetWebcastReportQuery({
    variables: { webcastId, reportType },
    skip: !webcastId || !reportType,
  });

  const report = data?.reportMetadata?.__typename === 'Report' ? data.reportMetadata : undefined;
  const downloadLink = report?.downloadLink || undefined;
  const isReportPending = report?.status === ReportStatus.SCHEDULED || report?.status === ReportStatus.CREATED;
  const isReportStatusNoData = report?.status === ReportStatus.NO_DATA;
  const isReportForbidden = data?.reportMetadata?.__typename === 'NotAuthorized';

  const [generateReportMutation, { loading: generating }] = useGenerateReportMutation();

  const downloadReport = async () => {
    if (!downloadLink) return;

    try {
      await keycloak.updateToken(300);
    } catch {
      keycloak.login();
      return;
    }

    downloadFile({
      url: downloadLink,
      token: keycloak.token,
    });
  };

  const handleAction = async () => {
    if (disabled || isReportForbidden) return;

    if (downloadLink) {
      await downloadReport();
      return;
    }

    generateReportMutation({
      variables: {
        input: {
          webcastId,
          type: reportType,
        },
      },
      onCompleted: (data) => {
        if (data?.generateReport?.__typename === 'GenerateReportSuccess') {
          refetch();
        }
      },
    });
  };

  useEffect(() => {
    if (isReportPending) {
      const interval = setInterval(() => {
        refetch();
      }, 10000);

      return () => clearInterval(interval);
    }
  }, [isReportPending, refetch]);

  const getReportStatusMessage = (statusReport?: ReportStatus) => {
    switch (statusReport) {
      case undefined:
      case ReportStatus.EXPIRED:
      case ReportStatus.FAILED:
        return t('manager.eventSummary.reports.tooltip.generateReport');
      case ReportStatus.CREATED:
      case ReportStatus.SCHEDULED:
        return t('manager.eventSummary.reports.tooltip.scheduledReport');
      case ReportStatus.NO_DATA:
        return t('manager.eventSummary.reports.tooltip.noData');
      case ReportStatus.READY:
        return t('manager.eventSummary.reports.tooltip.downloadReport');
      default:
        return undefined;
    }
  };

  const tooltipLabel = disabled
    ? disabledTooltipLabel
    : isReportForbidden
      ? t('manager.eventSummary.reports.tooltip.adminOnlyAttendanceReport')
      : isEventReportingAllowed
        ? getReportStatusMessage(report?.status)
        : t('manager.webcastSetup.notEditableMessage.role');

  const isDownloadButtonDisable =
    loading ||
    generating ||
    disabled ||
    isReportForbidden ||
    !isEventReportingAllowed ||
    isReportStatusNoData ||
    isReportPending;

  const isGenerating = loading || generating || isReportPending;

  const getReportTypeTestId = (type: ReportType): string => {
    const reportTypeMap: Record<ReportType, string> = {
      [ReportType.VIEWS_REPORT]: 'views',
      [ReportType.REGISTERED_USERS_REPORT]: 'registered-users',
      [ReportType.CHAT_MESSAGES_REPORT]: 'chat-messages',
    };
    return reportTypeMap[type] || 'unknown';
  };

  const reportTypeTestId = getReportTypeTestId(reportType);
  const baseTestId = `download-report-${reportTypeTestId}`;

  return (
    <div className={styles.wrapper}>
      <div className={styles.labelWrapper}>
        <Paragraph data-testid={`${baseTestId}-label`}>{downloadLink ? label : generateLabel}</Paragraph>
        <InfoTooltip label={infoText} />
      </div>

      <Tooltip label={tooltipLabel}>
        <IconButton
          disabled={isDownloadButtonDisable}
          onClick={handleAction}
          data-testid={baseTestId}
          aria-label={downloadLink ? label : generateLabel}
          className={styles.downloadButton}
          {...props}
        >
          {isGenerating ? (
            <Spinner size={18} style={{ margin: 'auto' }} />
          ) : downloadLink ? (
            <DownloadIcon data-testid={`${baseTestId}-download-icon`} />
          ) : (
            <PlusIcon data-testid={`${baseTestId}-plus-icon`} />
          )}
        </IconButton>
      </Tooltip>
    </div>
  );
}
